import { useLocation, useNavigate } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/shadcn/button";
import {
  Bell,
  LogOut,
  Users,
  LayoutDashboard,
  Coins,
  Wallet,
  Key,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/shadcn/dropdown-menu";
import { useAuth } from "@/contexts/AuthContext";
import { ChangePasswordDialog } from "@/components/auth/ChangePasswordDialog";
import { useState } from "react";

export function Header() {
  const { user, logout } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const [isPasswordDialogOpen, setIsPasswordDialogOpen] = useState(false);

  const handleLogout = () => {
    logout();
    toast({
      description: "You have been logged out successfully.",
      variant: "info",
    });
  };

  // Get user initials for avatar
  const getInitials = (name: string) => {
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase();
  };

  // Determine the current page and navigation items
  const currentPath = location.pathname;
  // Helper: is the user on a dashboard tab?
  const isDashboardTab = [
    "/active-quotes",
    "/accepted-quotes",
    "/trades"
  ].some((p) => currentPath.startsWith(p));
  const navigationItems = [
    {
      path: "/active-quotes",
      label: "Main Dashboard",
      icon: <LayoutDashboard className="h-5 w-5 mr-2" />,
    },
    {
      path: "/clients",
      label: "Clients",
      icon: <Users className="h-5 w-5 mr-2" />,
    },
    {
      path: "/assets",
      label: "Assets",
      icon: <Wallet className="h-5 w-5 mr-2" />,
    },
    {
      path: "/virtual-markets",
      label: "Virtual Markets",
      icon: <Wallet className="h-5 w-5 mr-2" />,
    },
  ];

  // Add Users menu item only for admin users
  if (user?.role === "admin") {
    navigationItems.push({
      path: "/users",
      label: "Users",
      icon: <Users className="h-5 w-5 mr-2" />,
    });
  }

  const isStagingOrDev =
    import.meta.env.VITE_ENV === "staging" || import.meta.env.VITE_ENV === "dev";

  return (
    <>
      {/* If env is staging or dev, add space of 11px for the banner */}
      <header
        className={`sticky z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 ${
          isStagingOrDev ? "top-11" : "top-0"
        }`}
      >
        <div className="container flex h-20 items-center justify-between">
          <div className="flex items-center">
            <img src="/logo_bax.png" alt="BAX Logo" className="h-8 mr-3" />
            <span className="font-[165] text-gray-600 tracking-[0.3em] text-4xl">
              OTC
            </span>
          </div>

          {/* Header right items */}
          <div className="flex items-center space-x-4">
            {/* Navigation Buttons */}
            <div className="flex items-center space-x-2">
              {navigationItems.map((item) => {
                // Special handling for Main Dashboard button
                if (item.label === "Main Dashboard") {
                  return (
                    <button
                      key={item.path}
                      onClick={() => {
                        if (!isDashboardTab) navigate(item.path);
                      }}
                      className={`px-4 py-2 rounded-lg text-md font-medium transition-all flex items-center
                        ${
                          isDashboardTab
                            ? "bg-black text-white shadow-sm hover:bg-gray-800"
                            : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                        }`}
                      disabled={isDashboardTab}
                    >
                      {item.icon}
                      {item.label}
                    </button>
                  );
                }
                // Other nav items
                return (
                  <button
                    key={item.path}
                    onClick={() => navigate(item.path)}
                    className={`px-4 py-2 rounded-lg text-md font-medium transition-all flex items-center
                      ${
                        currentPath === item.path
                          ? "bg-black text-white shadow-sm hover:bg-gray-800"
                          : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                      }`}
                  >
                    {item.icon}
                    {item.label}
                  </button>
                );
              })}
            </div>

            {/* Notifications */}
            <Button variant="ghost" size="icon">
              <Bell className="h-5 w-5" />
            </Button>

            {/* User Menu */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <div className="flex items-center space-x-2 cursor-pointer">
                  <div className="h-8 w-8 rounded-full bg-gray-100 text-gray-700 flex items-center justify-center">
                    <span className="text-sm">
                      {user?.name ? getInitials(user.name) : "?"}
                    </span>
                  </div>
                  <span className="hidden md:inline-block text-sm font-medium">
                    {user?.name || "User"}
                  </span>
                </div>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>My Account</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem className="text-muted-foreground">
                  {user?.email}
                </DropdownMenuItem>
                <DropdownMenuItem className="text-muted-foreground">
                  Role: {user?.role}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={() => setIsPasswordDialogOpen(true)}
                  className="cursor-pointer"
                >
                  <Key className="h-4 w-4 mr-2" />
                  Change Password
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={handleLogout}
                  className="text-red-600 focus:text-red-600 cursor-pointer"
                >
                  <LogOut className="h-4 w-4 mr-2" />
                  Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      <ChangePasswordDialog
        open={isPasswordDialogOpen}
        onOpenChange={setIsPasswordDialogOpen}
      />
    </>
  );
}
